import api from '../../../lib/api'

export interface PlatformAuditEntry {
  id: number
  action: string
  entity_type: string
  entity_id: string | null
  tenant_id: number | null
  tenant_name: string | null
  actor: string
  changes: Record<string, unknown> | null
  timestamp: string
}

export interface Tenant {
  id: number
  name: string
  domain: string
  is_active: boolean
  subscription_tier: string
  feature_flags: Record<string, unknown>
  settings: Record<string, unknown> | null
  created_at: string
}

export interface AppUser {
  id: number
  tenant_id: number
  email: string
  mobile: string
  full_name: string
  role: string
  is_active: boolean
  language: string
  last_login_at: string | null
  created_at: string
}

export interface Wallet {
  id: number
  tenant_id: number
  balance: number
  currency: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface WalletTransaction {
  id: number
  transaction_type: 'CREDIT' | 'DEBIT' | 'REFUND'
  amount: number
  balance_after: number
  description: string
  reference: string | null
  actor_name: string
  created_at: string
}

export interface DealerOnboardRequest {
  tenant_name: string
  domain: string
  subscription_tier?: string   // omit to leave blank until plan is assigned explicitly
  dp_full_name: string
  dp_email: string
  dp_mobile: string
  dp_password: string
  initial_wallet_credit?: number
  credit_description?: string
}

export interface DealerOnboardResponse {
  tenant: Tenant
  dealer_principal: AppUser
  wallet: Wallet
}

export interface CreateUserRequest {
  full_name: string
  email: string
  mobile: string
  password: string
  role: string
  tenant_id?: number
  language?: string
}

export interface TopUpRequest {
  amount: number
  description: string
  reference?: string
}

export interface TelephonyConfig {
  tenant_id: number
  provider: string
  account_sid: string
  subdomain: string
  api_key_hint: string
  virtual_number: string
  call_time_limit: number
  ring_timeout: number
  is_active: boolean
  is_configured: boolean
  updated_at: string | null
  call_recording_mode?: 'android_native' | 'sip'
  android_oem?: 'samsung' | 'xiaomi' | 'realme'
}

export type TelephonyProvider = 'exotel' | 'twilio' | 'myoperator' | 'cloudshope' | 'cloudshope_outbound'

export interface TelephonyConfigUpsert {
  provider: TelephonyProvider
  account_sid: string
  subdomain: string
  api_key: string
  api_secret: string
  virtual_number: string
  webhook_token: string
  call_time_limit?: number
  ring_timeout?: number
  call_recording_mode?: 'android_native' | 'sip'
  android_oem?: 'samsung' | 'xiaomi' | 'realme'
}

export interface Holiday {
  date: string
  label: string
}

export interface WorkingCalendar {
  working_days: number[]   // 0=Sun..6=Sat
  holidays: Holiday[]
}

export interface WhatsAppConfig {
  tenant_id: number
  phone_number_id: string
  api_token_hint: string
  business_name: string | null
  contact_phone: string | null
  is_active: boolean
  updated_at: string | null
}

export interface WhatsAppConfigUpsert {
  phone_number_id: string
  api_token: string
  business_name?: string
  contact_phone?: string
}

export interface TenantThemeConfig {
  app_name?: string
  tagline?: string
  primary_color?: string
  logo_url?: string | null
}

export const adminService = {
  // Tenants
  async listTenants(): Promise<Tenant[]> {
    const { data } = await api.get('/admin/tenants')
    return data
  },
  async updateTenant(id: number, payload: Partial<{ is_active: boolean; subscription_tier: string; name: string; domain: string }>): Promise<Tenant> {
    const { data } = await api.patch(`/admin/tenants/${id}`, payload)
    return data
  },
  async updateTenantTheme(id: number, theme: TenantThemeConfig): Promise<{ ok: boolean; theme: TenantThemeConfig }> {
    const { data } = await api.patch(`/admin/tenants/${id}/theme`, theme)
    return data
  },
  async uploadLogo(id: number, file: File): Promise<{ logo_url: string }> {
    const form = new FormData()
    form.append('file', file)
    const { data } = await api.post(`/admin/tenants/${id}/logo`, form, {
      headers: { 'Content-Type': undefined },
    })
    return data
  },
  async updateFeatureFlags(id: number, flags: Record<string, unknown>): Promise<Tenant> {
    const { data } = await api.patch(`/admin/tenants/${id}/features`, flags)
    return data
  },
  async deleteTenant(id: number): Promise<void> {
    await api.delete(`/admin/tenants/${id}`)
  },
  async onboardDealer(payload: DealerOnboardRequest): Promise<DealerOnboardResponse> {
    const { data } = await api.post('/admin/dealers/onboard', payload)
    return data
  },

  // Users
  async listUsers(): Promise<AppUser[]> {
    const { data } = await api.get('/admin/users')
    return data
  },
  async createUser(payload: CreateUserRequest): Promise<AppUser> {
    const { data } = await api.post('/admin/users', payload)
    return data
  },
  async updateUser(id: number, payload: Partial<{
    is_active: boolean; role: string
    full_name: string; mobile: string; new_password: string
  }>): Promise<AppUser> {
    const { data } = await api.patch(`/admin/users/${id}`, payload)
    return data
  },

  // Wallets
  async listWallets(): Promise<Wallet[]> {
    const { data } = await api.get('/admin/wallets')
    return data
  },
  async topUpWallet(tenantId: number, payload: TopUpRequest): Promise<Wallet> {
    const { data } = await api.post(`/admin/wallets/${tenantId}/topup`, payload)
    return data
  },
  async listTransactions(tenantId: number): Promise<WalletTransaction[]> {
    const { data } = await api.get(`/admin/wallets/${tenantId}/transactions`)
    return data
  },

  // Dealer Principal self-service wallet
  async getMyWallet(): Promise<Wallet> {
    const { data } = await api.get('/admin/wallet')
    return data
  },
  async getMyTransactions(): Promise<WalletTransaction[]> {
    const { data } = await api.get('/admin/wallet/transactions')
    return data
  },

  // Channel config — Telephony
  async getTelephonyConfig(tenantId: number): Promise<TelephonyConfig> {
    const { data } = await api.get(`/admin/tenants/${tenantId}/telephony-config`)
    return data
  },
  async upsertTelephonyConfig(tenantId: number, payload: TelephonyConfigUpsert): Promise<TelephonyConfig> {
    const { data } = await api.put(`/admin/tenants/${tenantId}/telephony-config`, payload)
    return data
  },

  // Working calendar — working days + holidays (drives WhatsApp callback slots)
  async getWorkingCalendar(tenantId: number): Promise<WorkingCalendar> {
    const { data } = await api.get(`/admin/tenants/${tenantId}/working-calendar`)
    return data
  },
  async updateWorkingDays(tenantId: number, workingDays: number[]): Promise<WorkingCalendar> {
    const { data } = await api.put(`/admin/tenants/${tenantId}/working-calendar/working-days`, { working_days: workingDays })
    return data
  },
  async addHoliday(tenantId: number, holiday: Holiday): Promise<WorkingCalendar> {
    const { data } = await api.post(`/admin/tenants/${tenantId}/working-calendar/holidays`, holiday)
    return data
  },
  async removeHoliday(tenantId: number, date: string): Promise<WorkingCalendar> {
    const { data } = await api.delete(`/admin/tenants/${tenantId}/working-calendar/holidays/${date}`)
    return data
  },

  // Lead assignment settings
  async getLeadAssignmentSettings(tenantId: number): Promise<{ manual_assignable_roles: string[] }> {
    const { data } = await api.get(`/admin/tenants/${tenantId}/lead-assignment-settings`)
    return data
  },
  async updateLeadAssignmentSettings(tenantId: number, roles: string[]): Promise<{ manual_assignable_roles: string[] }> {
    const { data } = await api.put(`/admin/tenants/${tenantId}/lead-assignment-settings`, { manual_assignable_roles: roles })
    return data
  },

  // Platform audit log (BACKEND_ADMIN only)
  async getPlatformAuditLogs(params?: {
    tenant_id?: number; entity_type?: string; action?: string; page?: number
  }): Promise<{ page: number; page_size: number; items: PlatformAuditEntry[] }> {
    const { data } = await api.get('/audit/platform', { params })
    return data
  },

  // Channel config — WhatsApp
  async getWhatsAppConfig(tenantId: number): Promise<WhatsAppConfig> {
    const { data } = await api.get(`/admin/tenants/${tenantId}/whatsapp-config`)
    return data
  },
  async upsertWhatsAppConfig(tenantId: number, payload: WhatsAppConfigUpsert): Promise<WhatsAppConfig> {
    const { data } = await api.put(`/admin/tenants/${tenantId}/whatsapp-config`, payload)
    return data
  },
}
